// Recurrence configuration block for the Add/Edit Task form.
//
// Self-contained — owns its own state and emits a `value` (= recurrence
// payload) plus an `enabled` flag through onChange. The parent form
// builds a payload for POST /api/v2/task-recurrence-rules from these
// values when `enabled === true`, otherwise it saves a one-off
// work_order through the existing /api/v2/workorders path.
//
// Field shapes mirror task_recurrence_rules.service.js validators in
// the backend. Adding fields here without matching backend support
// will produce 400 ValidationError responses on save.
import { useEffect, useMemo, useRef, useState } from "react";
import UIkit from "uikit";

const REMINDER_INFO_TEXT =
  "Day reminders are sent at 8:00 AM in the property's timezone.";

const FREQUENCIES = [
  { value: "daily",     label: "Daily" },
  { value: "weekly",    label: "Weekly" },
  { value: "monthly",   label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly",    label: "Yearly" },
];

const RECURRENCE_MODES = [
  { value: "calendar", label: "Calendar Date" },
  { value: "anchor",   label: "Guest Stay" },
];

const WEEKDAYS = [
  { value: 0, short: "Sun" },
  { value: 1, short: "Mon" },
  { value: 2, short: "Tue" },
  { value: 3, short: "Wed" },
  { value: 4, short: "Thu" },
  { value: 5, short: "Fri" },
  { value: 6, short: "Sat" },
];

const MONTHS = [
  { value: 1,  label: "January" },
  { value: 2,  label: "February" },
  { value: 3,  label: "March" },
  { value: 4,  label: "April" },
  { value: 5,  label: "May" },
  { value: 6,  label: "June" },
  { value: 7,  label: "July" },
  { value: 8,  label: "August" },
  { value: 9,  label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const ANCHOR_TYPES = [
  { value: "checkout_plus", label: "After guest checkout" },
  { value: "checkin_minus", label: "Before guest check-in" },
];

const DEFAULT_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_) {
    return "UTC";
  }
})();

export const DEFAULT_REMINDERS = [{ type: "day", days_before: 1, at_hour: 8 }];

export const REMINDER_OPTIONS = [
  { key: "hours:1", label: "1 hour before", reminder: { type: "hours_before", hours: 1 } },
  { key: "hours:2", label: "2 hours before", reminder: { type: "hours_before", hours: 2 } },
  { key: "hours:3", label: "3 hours before", reminder: { type: "hours_before", hours: 3 } },
  { key: "day:1", label: "1 day before", reminder: { type: "day", days_before: 1, at_hour: 8 } },
  { key: "day:2", label: "2 days before", reminder: { type: "day", days_before: 2, at_hour: 8 } },
  { key: "day:3", label: "3 days before", reminder: { type: "day", days_before: 3, at_hour: 8 } },
  { key: "day:7", label: "1 week before", reminder: { type: "day", days_before: 7, at_hour: 8 } },
];

const closestOptionKey = (options, value, getValue) => {
  let closest = options[0];
  let minDiff = Math.abs(value - getValue(closest));
  for (const opt of options) {
    const diff = Math.abs(value - getValue(opt));
    if (diff < minDiff) {
      minDiff = diff;
      closest = opt;
    }
  }
  return closest.key;
};

/** Map a stored reminder to the flat dropdown key (falls back to closest option). */
export function reminderToOptionKey(reminder) {
  if (!reminder?.type) return "day:1";

  if (reminder.type === "hours_before") {
    const hours = Number(reminder.hours);
    const exact = REMINDER_OPTIONS.find(
      (o) => o.reminder.type === "hours_before" && o.reminder.hours === hours,
    );
    if (exact) return exact.key;
    const hourOpts = REMINDER_OPTIONS.filter((o) => o.reminder.type === "hours_before");
    return closestOptionKey(hourOpts, hours, (o) => o.reminder.hours);
  }

  if (reminder.type === "day") {
    const days = Number(reminder.days_before);
    const exact = REMINDER_OPTIONS.find(
      (o) => o.reminder.type === "day" && o.reminder.days_before === days,
    );
    if (exact) return exact.key;
    const dayOpts = REMINDER_OPTIONS.filter((o) => o.reminder.type === "day");
    return closestOptionKey(dayOpts, days, (o) => o.reminder.days_before);
  }

  return "day:1";
}

/** Map a dropdown key to the API reminder object. */
export function optionKeyToReminder(key) {
  const opt = REMINDER_OPTIONS.find((o) => o.key === key);
  if (!opt) return { ...REMINDER_OPTIONS.find((o) => o.key === "day:1").reminder };
  return { ...opt.reminder };
}

export function summarizeReminders(reminders) {
  if (!Array.isArray(reminders) || reminders.length === 0) return "No reminders";
  return reminders
    .map((reminder) => {
      const optionKey = reminderToOptionKey(reminder);
      return REMINDER_OPTIONS.find((o) => o.key === optionKey)?.label || "1 day before";
    })
    .join(", ");
}

export function buildLockedSeriesMetaLine(summaryLine, reminders) {
  return `This schedule change only applies to this event. ${summaryLine}. Reminders: ${summarizeReminders(reminders)}`;
}

export const validateReminders = (reminders) => {
  if (!Array.isArray(reminders)) return "Reminders must be a list";
  if (reminders.length > 5) return "At most 5 reminders";
  for (const r of reminders) {
    if (r?.type === "day") {
      const days = Number(r.days_before);
      const hour = Number(r.at_hour);
      if (!Number.isInteger(days) || days < 0 || days > 30) {
        return "Day reminders: offset must be 0–30 days";
      }
      if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
        return "Day reminders: hour must be 0–23";
      }
    } else if (r?.type === "hours_before") {
      const hours = Number(r.hours);
      if (!Number.isInteger(hours) || hours < 1 || hours > 24) {
        return "Hour reminders: must be 1–24 hours before";
      }
    } else {
      return "Invalid reminder type";
    }
  }
  return null;
};

const DEFAULT_VALUE = {
  mode: "calendar",
  frequency: "daily",
  weekdays: [1], // Monday
  month_day: 1,
  year_month: new Date().getMonth() + 1, // 1..12 (yearly only)
  start_date: "",
  end_date: "",
  time_of_day: "09:00",
  timezone: DEFAULT_TZ,
  anchor_type: "checkout_plus",
  anchor_offset_days: 0,
  lookahead_count: 30,
  reminders: DEFAULT_REMINDERS,
};

// Build the API payload for POST /api/v2/task-recurrence-rules from the
// internal recurrence state.
//
// Yearly note: backend determines the recurrence month from
// start_date.month() and uses month_day for the day. For the yearly UI
// we let the user pick Month + Day directly, then synthesize a
// start_date whose month matches that selection so the backend
// schedules on the correct month.
export const buildRulePayload = (value, { name, summary, task_type, priority, category, description, rental_ids, default_assignees, checklist_template_id }) => {
  const base = {
    ...(value.assignment_mode ? {assignment_mode:value.assignment_mode} : {}),
    name: name || summary || "Recurring task",
    description: description || null,
    task_type,
    priority: priority || "medium",
    category: category || null,
    summary_template: summary,
    description_template: description || null,
    mode: value.mode,
    rental_scope: value.rental_scope || (rental_ids && rental_ids.length ? "rentals" : "all_rentals"),
    rental_ids: rental_ids && rental_ids.length ? rental_ids : undefined,
    time_of_day: value.time_of_day || "09:00",
    timezone: value.timezone || DEFAULT_TZ,
    lookahead_count: Number(value.lookahead_count) || 30,
    default_assignees: default_assignees || [],
    checklist_template_id: checklist_template_id || null,
    reminders: Array.isArray(value.reminders) ? value.reminders : DEFAULT_REMINDERS,
  };

  if (value.mode === "calendar") {
    base.frequency = value.frequency;
    base.interval = Number(value.interval) || 1;

    if (value.frequency === "weekly") {
      base.weekdays = (value.weekdays && value.weekdays.length) ? value.weekdays : [1];
    }

    if (["monthly", "quarterly"].includes(value.frequency) && value.month_day) {
      base.month_day = Number(value.month_day);
    }

    if (value.frequency === "yearly") {
      const day = Number(value.month_day) || 1;
      const month = Number(value.year_month) || (new Date().getMonth() + 1);
      base.month_day = day;
      // Force start_date to align with the selected month so backend
      // expansion (which keys off start_date.month()) schedules in the
      // correct month every year.
      const baseDate = value.start_date && /^\d{4}-\d{2}-\d{2}$/.test(value.start_date)
        ? value.start_date
        : `${new Date().getFullYear()}-01-01`;
      const year = baseDate.slice(0, 4);
      const mm = String(month).padStart(2, "0");
      const dd = String(Math.min(day, 28)).padStart(2, "0");
      base.start_date = `${year}-${mm}-${dd}`;
    } else if (value.start_date) {
      base.start_date = value.start_date;
    }

    if (value.end_date) base.end_date = value.end_date;
  } else {
    base.anchor_type = value.anchor_type;
    base.anchor_offset_days = Number(value.anchor_offset_days) || 0;
  }

  return base;
};

const TaskRecurrenceFields = ({
  value,
  onChange,
  errors = {},
  seriesFieldsEditable = true,
  occurrenceSchedule = null,
  allowModeToggle = true,
}) => {
  // `value` shape: { enabled: bool, ...DEFAULT_VALUE fields }
  const [local, setLocal] = useState(() => ({
    enabled: false,
    ...DEFAULT_VALUE,
    ...(value || {}),
  }));
  const valueSyncKey = value?._syncKey || "default";
  const containerRef = useRef(null);
  const infoDropdownIdRef = useRef(null);
  const localRef = useRef(local);
  const onChangeRef = useRef(onChange);
  const skipClickTargetRef = useRef(null);

  localRef.current = local;
  onChangeRef.current = onChange;

  if (!infoDropdownIdRef.current) {
    infoDropdownIdRef.current = `hms-reminder-info-${Math.random().toString(36).slice(2, 9)}`;
  }
  const infoDropdownId = infoDropdownIdRef.current;

  useEffect(() => {
    if (!local.enabled || !containerRef.current) return;
    UIkit.update(containerRef.current);
  }, [local.enabled, valueSyncKey, local.reminders?.length]);

  useEffect(() => {
    const next = {
      enabled: false,
      ...DEFAULT_VALUE,
      ...(value || {}),
    };
    setLocal((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSyncKey]);

  const set = (patch) => {
    const next = typeof patch === "function"
      ? patch(localRef.current)
      : { ...localRef.current, ...patch };
    localRef.current = next;
    setLocal(next);
    onChangeRef.current && onChangeRef.current(next);
  };

  const toggleWeekday = (d) => {
    set((current) => {
      const next = new Set(current.weekdays || []);
      if (next.has(d)) next.delete(d); else next.add(d);
      return { ...current, weekdays: Array.from(next).sort((a, b) => a - b) };
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const applyButtonAction = (target) => {
      const action = target.dataset.recurrenceAction;
      const rawValue = target.dataset.recurrenceValue;

      if (action === "enabled") {
        set({ enabled: rawValue === "true" });
      } else if (action === "mode") {
        set({ mode: rawValue });
      } else if (action === "frequency") {
        set({ frequency: rawValue });
      } else if (action === "weekday") {
        toggleWeekday(Number(rawValue));
      }
    };

    const handleMouseDown = (event) => {
      const target = event.target.closest?.("button[data-recurrence-action]");
      if (!target || !container.contains(target)) return;
      event.preventDefault();
      skipClickTargetRef.current = target;
      applyButtonAction(target);
    };

    const handleClick = (event) => {
      const target = event.target.closest?.("[data-recurrence-action]");
      if (!target || !container.contains(target)) return;

      if (skipClickTargetRef.current === target) {
        skipClickTargetRef.current = null;
        return;
      }

      applyButtonAction(target);
    };

    const handleFieldChange = (event) => {
      const target = event.target.closest?.("[data-recurrence-field]");
      if (!target || !container.contains(target)) return;

      const field = target.dataset.recurrenceField;
      const numberFields = new Set(["year_month"]);
      set({ [field]: numberFields.has(field) ? Number(target.value) : target.value });
    };

    container.addEventListener("mousedown", handleMouseDown, true);
    container.addEventListener("click", handleClick, true);
    container.addEventListener("input", handleFieldChange, true);
    container.addEventListener("change", handleFieldChange, true);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown, true);
      container.removeEventListener("click", handleClick, true);
      container.removeEventListener("input", handleFieldChange, true);
      container.removeEventListener("change", handleFieldChange, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showCalendar = local.mode === "calendar";
  const showAnchor   = local.mode === "anchor";
  const showWeekdays = showCalendar && local.frequency === "weekly";
  const showMonthDay = showCalendar && ["monthly", "quarterly"].includes(local.frequency);
  const showYearly   = showCalendar && local.frequency === "yearly";

  const reminders = Array.isArray(local.reminders) ? local.reminders : DEFAULT_REMINDERS;
  const reminderError = validateReminders(reminders);

  const replaceReminder = (index, reminder) => {
    set((current) => {
      const next = [...(current.reminders || DEFAULT_REMINDERS)];
      next[index] = reminder;
      return { ...current, reminders: next };
    });
  };

  const addReminder = () => {
    set((current) => {
      const list = [...(current.reminders || DEFAULT_REMINDERS)];
      if (list.length >= 5) return current;
      return {
        ...current,
        reminders: [...list, { type: "day", days_before: 1, at_hour: 8 }],
      };
    });
  };

  const removeReminder = (index) => {
    set((current) => {
      const list = [...(current.reminders || DEFAULT_REMINDERS)];
      list.splice(index, 1);
      return { ...current, reminders: list };
    });
  };

  const summaryLine = useMemo(() => {
    if (!local.enabled) return "One-time task";
    if (showAnchor) {
      const at = ANCHOR_TYPES.find((a) => a.value === local.anchor_type)?.label || local.anchor_type;
      const offset = Number(local.anchor_offset_days) || 0;
      return `${at}, ${offset} day${offset === 1 ? "" : "s"} offset, at ${local.time_of_day}`;
    }
    let s = ({
      daily: "Daily",
      weekly: "Weekly",
      monthly: "Monthly",
      quarterly: "Quarterly",
      yearly: "Yearly",
    })[local.frequency] || local.frequency;
    if (showWeekdays && local.weekdays?.length) {
      s += ` on ${local.weekdays.map((d) => WEEKDAYS[d].short).join(", ")}`;
    }
    if (showMonthDay && local.month_day) {
      s += ` on day ${local.month_day}`;
    }
    if (showYearly) {
      const monthLabel = MONTHS.find((m) => m.value === Number(local.year_month))?.label || "";
      s += ` on ${monthLabel} ${local.month_day || 1}`;
    }
    s += ` at ${local.time_of_day}`;
    return s;
  }, [local, showAnchor, showWeekdays, showMonthDay, showYearly]);

  const dateGridClassName = `hms-recurrence-date-grid ${showMonthDay || showYearly ? "hms-recurrence-date-grid--split" : "hms-recurrence-date-grid--single-row"}`;
  const selectedYearMonthLabel = MONTHS.find((m) => m.value === Number(local.year_month))?.label || "";
  const selectedAnchorTypeLabel = ANCHOR_TYPES.find((a) => a.value === local.anchor_type)?.label || "";
  const showOccurrenceScheduleFields =
    Boolean(occurrenceSchedule) && !seriesFieldsEditable && local.enabled;
  const lockedSeriesMetaLine = buildLockedSeriesMetaLine(summaryLine, reminders);

  return (
    <div
      className={`uk-width-1-1 hms-task-recurrence${
        showOccurrenceScheduleFields ? " hms-task-recurrence--occurrence-edit" : ""
      }`}
      ref={containerRef}
    >
      {!showOccurrenceScheduleFields && (
        <label className="uk-form-label">Schedule</label>
      )}

      {showOccurrenceScheduleFields ? (
        <>
          <div className="hms-recurrence-occurrence-fields">
            <div className="hms-recurrence-field">
              <label className="uk-form-label" htmlFor="task-recurrence-occurrence-date">
                Scheduled Date
              </label>
              <div className="uk-form-controls">
                <input
                  id="task-recurrence-occurrence-date"
                  className="uk-input"
                  type="date"
                  value={occurrenceSchedule.scheduledDate || ""}
                  onChange={(e) => occurrenceSchedule.onScheduledDateChange(e.target.value)}
                />
              </div>
            </div>
            <div className="hms-recurrence-field">
              <label className="uk-form-label" htmlFor="task-recurrence-occurrence-time">
                Scheduled Time
              </label>
              <div className="uk-form-controls">
                <input
                  id="task-recurrence-occurrence-time"
                  className="uk-input"
                  type="time"
                  value={occurrenceSchedule.scheduledStartTime || ""}
                  onChange={(e) => occurrenceSchedule.onScheduledStartTimeChange(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="uk-text-meta hms-recurrence-locked-inline">
            {lockedSeriesMetaLine}
          </div>
          {errors.recurrence && (
            <div className="uk-text-danger uk-text-small">{errors.recurrence}</div>
          )}
        </>
      ) : (
        <>
      {seriesFieldsEditable ? (
        <div className="uk-form-controls hms-recurrence-schedule-choice">
          <label className="uk-margin-right">
            <input
              type="radio"
              name="task-recurrence-enabled"
              className="uk-radio uk-margin-small-right"
              disabled={!allowModeToggle}
              {...(allowModeToggle
                ? { "data-recurrence-action": "enabled", "data-recurrence-value": "false" }
                : {})}
              checked={!local.enabled}
              onChange={() => { if (allowModeToggle) set({ enabled: false }); }}
            />
            One-time
          </label>
          <label>
            <input
              type="radio"
              name="task-recurrence-enabled"
              className="uk-radio uk-margin-small-right"
              disabled={!allowModeToggle}
              {...(allowModeToggle
                ? { "data-recurrence-action": "enabled", "data-recurrence-value": "true" }
                : {})}
              checked={local.enabled}
              onChange={() => { if (allowModeToggle) set({ enabled: true }); }}
            />
            Recurring
          </label>
        </div>
      ) : !local.enabled ? (
        <div className="uk-text-meta">
          This schedule change only applies to this event.
        </div>
      ) : null}

      {local.enabled && (
        <>
          {!seriesFieldsEditable ? (
            <>
              <div className="hms-recurrence-locked-group">
                <div className="hms-recurrence-locked-line uk-text-meta">
                  {lockedSeriesMetaLine}
                </div>
              </div>
              {errors.recurrence && (
                <div className="uk-text-danger uk-text-small">{errors.recurrence}</div>
              )}
            </>
          ) : (
            <>
          <div className="hms-recurrence-section">
            <label className="uk-form-label">Anchor On</label>
            <div className="uk-form-controls">
              <div className="checklist-item-type-btn-group">
                {RECURRENCE_MODES.map((m) => (
                  <button
                    type="button"
                    key={m.value}
                    className={`checklist-item-type-btn ${local.mode === m.value ? "active" : ""}`}
                    data-recurrence-action="mode"
                    data-recurrence-value={m.value}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar mode: progressive options */}
          {showCalendar && (
            <>
              <div className="hms-recurrence-section">
                <label className="uk-form-label">Frequency</label>
                <div className="uk-form-controls">
                  <div className="checklist-item-type-btn-group">
                    {FREQUENCIES.map((f) => (
                      <button
                        type="button"
                        key={f.value}
                        className={`checklist-item-type-btn ${local.frequency === f.value ? "active" : ""}`}
                        data-recurrence-action="frequency"
                        data-recurrence-value={f.value}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="hms-recurrence-calendar-fields">
                {showWeekdays && (
                  <div>
                    <label className="uk-form-label">Repeat on</label>
                    <div className="uk-form-controls">
                      <div className="checklist-item-type-btn-group">
                        {WEEKDAYS.map((d) => {
                          const active = (local.weekdays || []).includes(d.value);
                          return (
                            <button
                              type="button"
                              key={d.value}
                              className={`checklist-item-type-btn ${active ? "active" : ""}`}
                              data-recurrence-action="weekday"
                              data-recurrence-value={d.value}
                            >
                              {d.short}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className={dateGridClassName}>
                  {showMonthDay && (
                    <div className="hms-recurrence-field">
                      <label className="uk-form-label">
                        {local.frequency === "quarterly" ? "Day of quarter" : "Day of month"}
                      </label>
                      <div className="uk-form-controls">
                        <input
                          type="number"
                          className="uk-input"
                          min={1}
                          max={31}
                          data-recurrence-field="month_day"
                          value={local.month_day}
                          onChange={(e) => set({ month_day: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  {showYearly && (
                    <>
                    <div className="hms-recurrence-field">
                      <label className="uk-form-label">Month</label>
                      <div className="uk-form-controls">
                        <div className="hms-recurrence-select-wrap">
                          <select
                            className="uk-select hms-recurrence-select"
                            data-recurrence-field="year_month"
                            value={local.year_month}
                            onChange={(e) => set({ year_month: Number(e.target.value) })}
                          >
                            {MONTHS.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <span className="hms-recurrence-select-value">{selectedYearMonthLabel}</span>
                        </div>
                      </div>
                    </div>
                    <div className="hms-recurrence-field">
                      <label className="uk-form-label">Day</label>
                      <div className="uk-form-controls">
                        <input
                          type="number"
                          className="uk-input"
                          min={1}
                          max={31}
                          data-recurrence-field="month_day"
                          value={local.month_day}
                          onChange={(e) => set({ month_day: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                  )}

                  <div className="hms-recurrence-field">
                    <label className="uk-form-label">Time of day</label>
                    <div className="uk-form-controls">
                      <input
                        type="time"
                        className="uk-input"
                        data-recurrence-field="time_of_day"
                        value={local.time_of_day}
                        onChange={(e) => set({ time_of_day: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="hms-recurrence-field">
                    <label className="uk-form-label">Start date</label>
                    <div className="uk-form-controls">
                      <input
                        type="date"
                        className="uk-input"
                        data-recurrence-field="start_date"
                        value={local.start_date}
                        onChange={(e) => set({ start_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="hms-recurrence-field">
                    <label className="uk-form-label">End date</label>
                    <div className="uk-form-controls">
                      <input
                        type="date"
                        className="uk-input"
                        data-recurrence-field="end_date"
                        value={local.end_date}
                        onChange={(e) => set({ end_date: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Anchor mode: trigger + offset + time */}
          {showAnchor && (
            <div className="hms-recurrence-anchor-fields">
              <div className="hms-recurrence-field">
                <label className="uk-form-label">Trigger</label>
                <div className="uk-form-controls">
                  <div className="hms-recurrence-select-wrap">
                    <select
                      className="uk-select hms-recurrence-select"
                      data-recurrence-field="anchor_type"
                      value={local.anchor_type}
                      onChange={(e) => set({ anchor_type: e.target.value })}
                    >
                      {ANCHOR_TYPES.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                    <span className="hms-recurrence-select-value">{selectedAnchorTypeLabel}</span>
                  </div>
                </div>
              </div>
              <div className="hms-recurrence-field">
                <label className="uk-form-label">Offset (days)</label>
                <div className="uk-form-controls">
                  <input
                    type="number"
                    className="uk-input"
                    min={0}
                    data-recurrence-field="anchor_offset_days"
                    value={local.anchor_offset_days}
                    onChange={(e) => set({ anchor_offset_days: e.target.value })}
                  />
                  <div className="uk-text-meta">
                    0 = same day; {local.anchor_type === "checkout_plus" ? "days after checkout" : "days before check-in"}
                  </div>
                </div>
              </div>
              <div className="hms-recurrence-field">
                <label className="uk-form-label">Time of day</label>
                <div className="uk-form-controls">
                  <input
                    type="time"
                    className="uk-input"
                    data-recurrence-field="time_of_day"
                    value={local.time_of_day}
                    onChange={(e) => set({ time_of_day: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="hms-recurrence-summary uk-text-meta">{summaryLine}</div>

          <div className="hms-recurrence-reminders uk-margin-top">
            <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small hms-recurrence-reminders-header">
              <div className="uk-flex uk-flex-middle hms-recurrence-reminders-label">
                <label className="uk-form-label uk-margin-remove-bottom">Reminders</label>
                <div className="uk-inline hms-recurrence-info-wrap">
                  <button
                    type="button"
                    className="uk-button uk-button-link hms-recurrence-info-btn"
                    aria-label="Reminder info"
                    uk-toggle={`target: #${infoDropdownId}; mode: click`}
                  >
                    <img className="info-icon" src="/images/info-icon.svg" alt="" />
                  </button>
                  <div
                    id={infoDropdownId}
                    uk-dropdown="mode: click; pos: bottom-left"
                  >
                    <div className="uk-padding-small uk-text-small">
                      {REMINDER_INFO_TEXT}
                    </div>
                  </div>
                </div>
              </div>
              {reminders.length < 5 && (
                <button
                  type="button"
                  className="uk-button hms-btn hms-gray-btn hms-recurrence-add-btn"
                  aria-label="Add reminder"
                  onClick={addReminder}
                >
                  +
                </button>
              )}
            </div>
            <div className="uk-form-controls">
              {reminders.map((reminder, index) => {
                const optionKey = reminderToOptionKey(reminder);
                const optionLabel =
                  REMINDER_OPTIONS.find((o) => o.key === optionKey)?.label || "1 day before";

                return (
                  <div
                    key={`reminder-${index}`}
                    className="hms-recurrence-reminder-row"
                  >
                    <div className="hms-recurrence-reminder-select">
                      <div uk-form-custom="target: > * > span:first-child">
                        <select
                          value={optionKey}
                          onChange={(e) =>
                            replaceReminder(index, optionKeyToReminder(e.target.value))
                          }
                        >
                          {REMINDER_OPTIONS.map((o) => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                          ))}
                        </select>
                        <button
                          className="uk-button uk-button-default"
                          type="button"
                          tabIndex={-1}
                        >
                          <span className="select-value">{optionLabel}</span>
                          <span
                            uk-icon="icon: chevron-down"
                            className="hms-align-right"
                          ></span>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="uk-button hms-simple-icon-btn"
                      aria-label="Delete reminder"
                      onClick={() => removeReminder(index)}
                    >
                      <img src="/images/delete-icon.svg" alt="Delete" />
                    </button>
                  </div>
                );
              })}
              {reminderError && (
                <div className="uk-text-danger uk-text-small uk-margin-small-top">{reminderError}</div>
              )}
            </div>
          </div>
          {errors.recurrence && (
            <div className="uk-text-danger uk-text-small">{errors.recurrence}</div>
          )}
            </>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
};

export default TaskRecurrenceFields;
